import React, { useState } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import keycloak, {
  CALLBACK_ROUTES,
  createKeycloakInitOptions,
  shouldInitKeycloak,
} from "./admin/auth/keycloak";
import RequireAuth from "./admin/auth/RequireAuth";
import { AdminProvider } from "./admin/AdminContext";
import { ToastProvider } from "./admin/context/ToastContext";
import { ConfirmProvider } from "./admin/context/ConfirmContext";
import AdminLayout from "./admin/AdminLayout";
import KeycloakCallback from "./admin/pages/KeycloakCallback";
import BlocksAdmin from "./admin/pages/BlocksAdmin";
import BlockEditor from "./admin/pages/BlockEditor/index";
import AuditLog from "./admin/pages/AuditLog";
import PapersAdmin from "./admin/pages/PapersAdmin";
import PaperEditor from "./admin/pages/PaperEditor";

const ROUTES = [
  { path: "/opredelitel/", element: <App /> },
  { path: "/opredelitel/:blockId", element: <App /> },
  { path: "*", element: <div>Page not found</div> }, // Обработчик 404
];

const AppRoutes = () => (
  <Routes>
    {ROUTES.map(({ path, element }, index) => (
      <Route key={index} path={path} element={element} />
    ))}

    {/* Keycloak callback: dev serves it from the root, prod from /opredelitel */}
    {CALLBACK_ROUTES.map((path) => (
      <Route key={path} path={path} element={<KeycloakCallback />} />
    ))}

    {/* Admin routes */}
    <Route
      path="/opredelitel/admin"
      element={
        <RequireAuth>
          <AdminProvider>
            <ToastProvider>
              <ConfirmProvider>
                <AdminLayout />
              </ConfirmProvider>
            </ToastProvider>
          </AdminProvider>
        </RequireAuth>
      }
    >
      <Route index element={<Navigate to="blocks" replace />} />
      <Route path="blocks" element={<BlocksAdmin />} />
      <Route path="blocks/:id" element={<BlockEditor />} />
      <Route path="papers" element={<PapersAdmin />} />
      <Route path="papers/:id" element={<PaperEditor />} />
      <Route path="logs" element={<AuditLog />} />
    </Route>
  </Routes>
);

/**
 * Mount Keycloak lazily, only on pages that need it.
 *
 * Initializing keycloak-js with `check-sso` on the public site would redirect
 * anonymous visitors through Keycloak, so public pages render without the
 * provider. Once Keycloak has been initialized it stays mounted for the rest
 * of the session (keycloak-js cannot be initialized twice).
 */
const KeycloakGate = ({ children }) => {
  const location = useLocation();
  const needsKeycloak = shouldInitKeycloak(location.pathname);
  const [enabled, setEnabled] = useState(needsKeycloak);
  const [initOptions, setInitOptions] = useState(() =>
    needsKeycloak ? createKeycloakInitOptions() : null,
  );

  if (needsKeycloak && !enabled) {
    // Adjusting state during render; React re-renders immediately and mounts
    // the provider synchronously on the first admin/callback navigation.
    setEnabled(true);
    setInitOptions(createKeycloakInitOptions());
    return null;
  }

  if (!enabled) {
    return children;
  }

  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={initOptions}
      onEvent={(event, error) => {
        if (event === "onInitError") {
          console.error("Keycloak init failed", error);
        }
      }}
    >
      {children}
    </ReactKeycloakProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <BrowserRouter>
    <KeycloakGate>
      <AppRoutes />
    </KeycloakGate>
  </BrowserRouter>,
);
